/* The ring's box and the figure in its hole.
 *
 * `--border` is re-pointed here rather than in `vendor.css`, and that is deliberate: bklit reads
 * that token for the UNFILLED part of the arc, and `--border` is a KANON hairline colour that is
 * correct everywhere else in the app. Overriding it globally would repaint every border bklit
 * touches; overriding it on this wrapper repaints exactly the track.
 *
 * `--bg-sunken`, which is KANON's own: both `Klienci v2 - D` and `Detal klienta v3` write the
 * empty arc as `T.sunken`. It stood at `--border-2` on a measured departure — Flame Dark's
 * `--bg-sunken` (#0f0f10) against `--bg-card` (#161618) measures 1.06:1, so an unmeasured goal
 * drew a track nobody could see — and the owner withdrew the measured departures on 1 September
 * 2026. The number is in the round's report so he can call this one back by name. */

.wrap {
  position: relative;
  display: grid;
  flex: none;
  place-items: center;

  --border: var(--bg-sunken);
}

/* The chart's box overhangs the ring by HALF the padding bklit reserves, on every side, and this is
 * the rule that centres it. `--ring-inset` is set inline by the component, already negative, off
 * the same constant it sizes the chart with — so the two cannot drift, and this file needs no
 * arithmetic. It is a computed geometry rather than a design value: the number belongs to bklit's
 * own padding, which is why it is not a token in `spacing.css`.
 *
 * It replaces `inset: 0`, which was a MEASURED defect rather than a style preference. `RingChart`
 * renders a box carrying an inline `width`/`height` of `diameter + SIZE_PADDING`, and an absolutely
 * positioned box with both `inset: 0` and an explicit width is over-constrained: the used value
 * keeps `left`/`top` and drops `right`/`bottom`, so a 78px chart hung off the top-left corner of a
 * 62px wrapper. Its centre landed 8px right and 8px down of the wrapper's — exactly half the
 * padding — and the figure, centred on the wrapper, therefore read 8px up and to the left of the
 * arc it belongs to. So did the caller's caption underneath.
 *
 * Grid-cell stacking was tried first and does NOT do it: the chart is a grid item WIDER than its
 * track, and Chrome start-aligns an overflowing item rather than centring it. Measured on the
 * running app, 1 September 2026.
 *
 * This element is OURS, not bklit's. The old rule sat on the chart itself, where it only worked
 * while this module's `position: absolute` came after Tailwind's `relative` in the built CSS —
 * a chunk-order dependency with no way to notice it had flipped. */
.chart {
  position: absolute;
  inset: var(--ring-inset);
  display: block;
}

.figure {
  position: relative;
  font-size: var(--fs-ring-figure);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-auto);
  color: var(--text);
  letter-spacing: var(--ls-display);
  font-variant-numeric: tabular-nums;
}

.figureWide {
  font-size: var(--fs-ring-figure-lg);
}

/* The Pulpit task card's 56px ring: "100%" with air between it and the stroke. */
.figureSmall {
  font-size: var(--fs-label-md);
}

/* The task card's key readout (KANON "Karty zadań", 24 September 2026): an 88px ring whose hole
 * carries the session's one big number. */
.figureKey {
  font-size: var(--fs-ring-figure-key);
}
